import { createReducer, on } from '@ngrx/store';
import { BaseColorDto } from '../../api/scheduler/models/base-color-dto';
import { BaseDto } from '../../api/scheduler/models/base-dto';
import { BaseLeadDto } from '../../api/scheduler/models/base-lead-dto';
import { BaseCrewRowDto } from '../../api/scheduler/models/base-crew-row-dto';
import { NamedColor } from '../../shared/utils/color-distance';
import { BaseManagementActions } from './base-management.actions';

export type BaseWriteKind = 'create' | 'update' | 'shifts' | 'image';

export interface BaseAccess {
  allowed: boolean;
  canWrite: boolean;
}

export interface BaseManagementState {
  /** Null until the access check answers; screens wait instead of flashing. */
  access: BaseAccess | null;
  /** The Bases on the requested page only. */
  bases: BaseDto[];
  totalElements: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  selected: BaseDto | null;
  detailLoading: boolean;
  /** True when the last base-detail request failed. */
  detailError: boolean;
  leads: BaseLeadDto[];
  timeZones: string[];
  /** Indication colours stored on Bases today, with the Base using each. */
  usedColors: BaseColorDto[];
  /** Event, Training, Vacation and Leave colours from the schedule legends; a Base must avoid them. */
  reservedColors: NamedColor[];
  /** Current page of crew rows from GET /api/scheduler/bases/{id}/crew. */
  crew: BaseCrewRowDto[];
  crewTotal: number;
  crewTotalPages: number;
  crewLoading: boolean;
  crewForbidden: boolean;
  saving: boolean;
  /** Bumped on every successful write so forms reset only after the store confirms it. */
  writeRevision: number;
  lastWrite: BaseWriteKind | null;
  lastLeadChange: { previousCrewId: number | null; baseId: number } | null;
}

export const initialBaseManagementState: BaseManagementState = {
  access: null,
  bases: [],
  totalElements: 0,
  totalPages: 0,
  loading: false,
  error: null,
  selected: null,
  detailLoading: false,
  detailError: false,
  leads: [],
  timeZones: [],
  usedColors: [],
  reservedColors: [],
  crew: [],
  crewTotal: 0,
  crewTotalPages: 1,
  crewLoading: false,
  crewForbidden: false,
  saving: false,
  writeRevision: 0,
  lastWrite: null,
  lastLeadChange: null,
};

function replaceBase(bases: BaseDto[], base: BaseDto): BaseDto[] {
  return bases.map((item) => (item.id === base.id ? base : item));
}

function withProfileImage(base: BaseDto, id: number, profileImageUrl: string | null): BaseDto {
  return base.id === id ? { ...base, profileImageUrl } : base;
}

function imageChanged(state: BaseManagementState, id: number, profileImageUrl: string | null) {
  return {
    ...state,
    saving: false,
    lastWrite: 'image' as const,
    bases: state.bases.map((base) => withProfileImage(base, id, profileImageUrl)),
    selected: state.selected ? withProfileImage(state.selected, id, profileImageUrl) : null,
  };
}

function writeSucceeded(state: BaseManagementState, base: BaseDto, kind: BaseWriteKind) {
  return {
    ...state,
    saving: false,
    writeRevision: state.writeRevision + 1,
    lastWrite: kind,
    // A created Base is picked up when the list reloads, so paging and totals stay the server's.
    bases: kind === 'create' ? state.bases : replaceBase(state.bases, base),
    selected: state.selected?.id === base.id ? base : state.selected,
  };
}

export const baseManagementReducer = createReducer(
  initialBaseManagementState,
  on(BaseManagementActions.loadAccessSuccess, (state, { allowed, canWrite }) => ({
    ...state,
    access: { allowed, canWrite: allowed && canWrite },
  })),
  on(BaseManagementActions.loadAccessFailure, (state) => ({
    ...state,
    access: { allowed: false, canWrite: false },
  })),
  on(BaseManagementActions.loadBases, (state) => ({ ...state, loading: true, error: null })),
  on(BaseManagementActions.loadBasesSuccess, (state, { bases, totalElements, totalPages }) => ({
    ...state,
    loading: false,
    bases,
    totalElements,
    totalPages,
  })),
  on(BaseManagementActions.loadBasesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(BaseManagementActions.loadBase, (state, { id }) => ({
    ...state,
    detailLoading: true,
    detailError: false,
    selected: state.selected?.id === id ? state.selected : null,
  })),
  on(BaseManagementActions.loadBaseSuccess, (state, { base }) => ({
    ...state,
    detailLoading: false,
    detailError: false,
    selected: base,
  })),
  on(BaseManagementActions.loadBaseFailure, (state) => ({
    ...state,
    detailLoading: false,
    detailError: true,
  })),
  on(BaseManagementActions.loadLookupsSuccess, (state, { leads, timeZones }) => ({
    ...state,
    leads,
    timeZones,
  })),
  on(BaseManagementActions.loadBaseColorsSuccess, (state, { colors }) => ({
    ...state,
    usedColors: colors,
  })),
  on(BaseManagementActions.loadReservedColorsSuccess, (state, { colors }) => ({
    ...state,
    reservedColors: colors,
  })),
  on(BaseManagementActions.loadCrew, (state) => ({
    ...state,
    crewLoading: true,
    crew: [],
  })),
  on(BaseManagementActions.loadCrewSuccess, (state, { crew, totalElements, totalPages }) => ({
    ...state,
    crewLoading: false,
    crew,
    crewTotal: totalElements,
    crewTotalPages: totalPages,
  })),
  on(BaseManagementActions.loadCrewFailure, (state, { forbidden }) => ({
    ...state,
    crewLoading: false,
    crewForbidden: forbidden ?? state.crewForbidden,
  })),
  on(BaseManagementActions.assignCrewLeadSuccess, (state, { baseId, newLead, previousCrewId }) => ({
    ...state,
    lastLeadChange: { previousCrewId, baseId },
    selected:
      state.selected?.id === baseId ? { ...state.selected, baseLead: newLead } : state.selected,
    crew: state.crew.map((c) => ({
      ...c,
      baseLead: c.crewId === newLead.userId,
    })),
  })),
  on(BaseManagementActions.revertCrewLeadSuccess, (state) => {
    const restoredCrewId = state.lastLeadChange?.previousCrewId ?? null;
    return {
      ...state,
      lastLeadChange: null,
      crew: state.crew.map((c) => ({
        ...c,
        baseLead: restoredCrewId != null && c.crewId === restoredCrewId,
      })),
    };
  }),
  on(
    BaseManagementActions.createBase,
    BaseManagementActions.updateBase,
    BaseManagementActions.createShift,
    BaseManagementActions.updateShifts,
    BaseManagementActions.uploadProfileImage,
    BaseManagementActions.deleteProfileImage,
    (state) => ({ ...state, saving: true })
  ),
  on(BaseManagementActions.createBaseSuccess, (state, { base }) =>
    writeSucceeded(state, base, 'create')
  ),
  on(BaseManagementActions.updateBaseSuccess, (state, { base }) =>
    writeSucceeded(state, base, 'update')
  ),
  on(
    BaseManagementActions.createShiftSuccess,
    BaseManagementActions.updateShiftsSuccess,
    (state, { base }) => writeSucceeded(state, base, 'shifts')
  ),
  on(BaseManagementActions.uploadProfileImageSuccess, (state, { id, profileImageUrl }) =>
    imageChanged(state, id, profileImageUrl)
  ),
  on(BaseManagementActions.deleteProfileImageSuccess, (state, { id }) =>
    imageChanged(state, id, null)
  ),
  on(BaseManagementActions.saveFailure, (state) => ({ ...state, saving: false }))
);
